import { API_PATH, CDN_PATH, ENV_NAME } from './url';

import { APISettingName, APIStringSearchFilter, PrintType } from 'types/api';

export const apiSpecEndpoint = () => `${API_PATH}docs`;
export const cdnEndpoint = () => `${CDN_PATH}`;

export const authThirdParty = (auth: 'google' | 'microsoft') =>
  `${
    ENV_NAME == 'dev' ? 'http://localhost:8080/' : API_PATH
  }auth/${auth}/login`;

// anonymous APIs
export const userEndpoint = () => `${API_PATH}user/`;
export const userAuthEndpoint = () => `${userEndpoint()}auth`;
export const userPasswordResetLinkEndpoint = (email: string) =>
  `${userEndpoint()}password-reset-link?email=${email}`;
export const validateUserResetTokenPasswordEndpoint = (
  email: string,
  token: string,
) => `${userEndpoint()}validate-reset-token?token=${token}&email=${email}`;
export const userResetPasswordEndpoint = () =>
  `${userEndpoint()}reset-password`;
export const userRefreshTokenEndpoint = () => `${userEndpoint()}token`;

export const anonymousPaymentEndpoint = (paymentId: string) =>
  `${API_PATH}payment/${paymentId}/`;

export const userPaymentDetailsEndpoint = (paymentId: string) =>
  `${anonymousPaymentEndpoint(paymentId)}details`;

export const anonymousPrintPaymentEndpoint = (id: string, type: PrintType) =>
  `${anonymousPaymentEndpoint(id)}download/${type}/`;

export const finishPrintingEndpoint = (id: string) =>
  `${anonymousPaymentEndpoint(id)}printed/`;

export const verifyCheckEndpoint = (id: string) =>
  `${API_PATH}payment/verify/${id}/`;

export const userInfoEndpoint = () => `${userEndpoint()}me`;

// user APIs
export const userAvatarEndpoint = () => `${userEndpoint()}avatar/`;
export const changePasswordEndpoint = () => `${userEndpoint()}change-password/`;
export const userSettingsEndpoint = () => `${userEndpoint()}settings`;
export const userPayeeEndpoint = () => `${API_PATH}payee/`;
export const payeeEditEndpoint = (payeeId: string) =>
  `${userPayeeEndpoint()}${payeeId}`;
export const userPaymentEndpoint = () => `${API_PATH}payment`;
export const globalPaymentSearchEndpoint = () =>
  `${userPaymentEndpoint()}/global-search`;

export const userOrganizationPayeeSearch = () =>
  `${userPayeeEndpoint()}/global-search`;

export const requestPaymentEndpoint = (organizationId: string) =>
  `${userPaymentEndpoint()}/${organizationId}/request`;

// organization APIs
export const baseEndpoint = (organizationId: string) =>
  `${organizationEndpoint()}${organizationId}/`;
export const organizationEndpoint = () => `${API_PATH}organization/`;
export const organizationSingleEndpoint = baseEndpoint;

export const payeeEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}payee/`;

export const payeesExport = (organizationId: string) =>
  `${payeeEndpoint(organizationId)}export/`;

export const payeeSingleEndpoint = (organizationId: string, payeeId: string) =>
  `${payeeEndpoint(organizationId)}${payeeId}/`;

export const paymentSingleEndpoint = (
  organizationId: string,
  paymentId: string,
) => `${baseEndpoint(organizationId)}payment/${paymentId}/`;

export const paymentDownloadEndpoint = (
  organizationId: string,
  paymentId: string,
  printType: PrintType,
) =>
  `${paymentSingleEndpoint(organizationId, paymentId)}download/${printType}/`;

export const paymentResendEndpoint = (
  organizationId: string,
  paymentId: string,
) => `${paymentSingleEndpoint(organizationId, paymentId)}resend/`;

export const paymentBulkResendEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}payment/bulk-resend`;

export const paymentEmailStatusEndpoint = (
  organizationId: string,
  paymentId: string,
) => `${paymentSingleEndpoint(organizationId, paymentId)}email-status/`;

export const paymentApproveEndpoint = (
  organizationId: string,
  paymentId: string,
) => `${paymentSingleEndpoint(organizationId, paymentId)}approve/`;

export const paymentsExport = (organizationId: string) =>
  `${baseEndpoint(organizationId)}payment/export/`;

export const paymentEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}payment`;

export const bankEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}bank/`;

export const organizationTypeaheadEndpoint = () =>
  `${API_PATH}organization/typeahead`;

export const userTypeaheadEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}user/typeahead`;

export const payeeTypeaheadEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}payee/typeahead`;

export const userListEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}user/`;

export const inviteUserEndpoint = (organizationId: string) =>
  `${userListEndpoint(organizationId)}invite/`;

export const userSingleEndpoint = (organizationId: string, userId: string) =>
  `${userListEndpoint(organizationId)}/${userId}/`;

export const userRightsEndpoint = (organizationId: string, userId: string) =>
  `${userSingleEndpoint(organizationId, userId)}rights/`;

export const bankSingleEndpoint = (organizationId: string, bankId: string) =>
  `${bankEndpoint(organizationId)}${bankId}/`;

export const blankCheckEndpoint = (organizationId: string, bankId: string) =>
  `${bankSingleEndpoint(organizationId, bankId)}blank-check`;

export const bankCheckTemplateEndpoint = (
  organizationId: string,
  bankId: string,
  type: PrintType,
) => `${bankSingleEndpoint(organizationId, bankId)}template/${type}/`;

export const bankTypeaheadEndpoint = (organizationId: string) =>
  `${bankEndpoint(organizationId)}typeahead/`;

// setting
export const settingEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}setting/`;

export const settingSingleEndpoint = (
  organizationId: string,
  name: APISettingName,
) => `${settingEndpoint(organizationId)}${name}`;

export const auditEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}audit/`;

export const apiClientTokenEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}api-token`;

export const apiClientTokenSingleEndpoint = (
  organizationId: string,
  tokenId: string,
) => `${apiClientTokenEndpoint(organizationId)}/${tokenId}`;

export const apiClientTokenToggleEndpoint = (
  organizationId: string,
  tokenId: string,
) => `${apiClientTokenSingleEndpoint(organizationId, tokenId)}/toggle`;

export type SuperAdminOrganizationFilter = {
  name?: APIStringSearchFilter;
  description?: APIStringSearchFilter;
  isSandbox?: boolean;
  isDisabled?: boolean;
};

const appendStringFilter = (
  params: URLSearchParams,
  field: string,
  filter?: APIStringSearchFilter,
) => {
  if (!filter?.value) return;

  params.set(field, filter.value);
  if (filter.condition) {
    params.set(`${field}Condition`, filter.condition);
  }
};

export const superAdminAllOrganizationsEndpoint = (
  filter?: SuperAdminOrganizationFilter,
) => {
  const params = new URLSearchParams();
  appendStringFilter(params, 'name', filter?.name);
  appendStringFilter(params, 'description', filter?.description);
  if (filter?.isSandbox !== undefined) {
    params.set('isSandbox', String(filter.isSandbox));
  }
  if (filter?.isDisabled !== undefined) {
    params.set('isDisabled', String(filter.isDisabled));
  }
  const query = params.toString();
  return `${API_PATH}organization/super-admin/all${query ? `?${query}` : ''}`;
};

export const superAdminOrgStatsEndpoint = (
  organizationId: string,
  period: string,
) =>
  `${API_PATH}organization/${organizationId}/super-admin/stats?period=${period}`;

export const superAdminOrgUsageEndpoint = (organizationId: string) =>
  `${API_PATH}organization/${organizationId}/super-admin/usage`;

export const superAdminOrgCreditEndpoint = (organizationId: string) =>
  `${API_PATH}organization/${organizationId}/super-admin/credit`;

export const superAdminOrgDisableEndpoint = (organizationId: string) =>
  `${API_PATH}organization/${organizationId}/super-admin/disable`;

export const superAdminOrgEnableEndpoint = (organizationId: string) =>
  `${API_PATH}organization/${organizationId}/super-admin/enable`;

// billing
export const billingEndpoint = (organizationId: string) =>
  `${baseEndpoint(organizationId)}billing/`;

export const billingOverviewEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}overview`;

export const billingEntitlementEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}entitlement`;

export const billingPlanEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}plan`;

export const billingPackTierEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}pack-tier`;

export const billingPackQuoteEndpoint = (
  organizationId: string,
  quantity: number,
) => `${billingEndpoint(organizationId)}pack-quote?quantity=${quantity}`;

export const billingPurchaseEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}purchase`;

export const billingInvoiceEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}invoice`;

export const billingTopUpEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}top-up`;

export const billingSubscribeEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}subscribe`;

export const billingPortalEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}portal`;

export const billingCancelEndpoint = (organizationId: string) =>
  `${billingEndpoint(organizationId)}cancel`;
